// apps/client/src/config/helpContent.ts
// Single source of truth for Help & Support page content + per-page in-app help.
// When RBAC permissions change, update THIS file — not the React components.

export type AppRole = 'super_admin' | 'administrator' | 'office_admin' | 'credit_analyst' | 'broker' | 'marketing';

// ============================================================================
// PER-PAGE HELP CONTENT
// ============================================================================
// Used by the reusable HelpButton / HelpDrawer / HelpTooltip primitives.
// To add a page to the in-app help system:
//   1. Add a literal to PageHelpKey
//   2. Add a PAGE_HELP[key] entry
//   3. On the page: <PageHeader actions={<HelpButton pageKey="..." />} />
//   4. On each StatCard: helpKey="..." + helpTooltip={cardHelp('...', '...')}

export type PageHelpKey =
  | 'trailRunOff'
  | 'commissions'
  | 'clientCare'
  | 'clientCareSettings'
  | 'team'
  | 'loanBookValue';

export interface CardHelp {
  /** Stable identifier used for telemetry (help_card_tooltip_opened.cardKey) */
  key: string;
  /** Must match the StatCard `title` prop on the page */
  title: string;
  /** Plain-language definition shown in tooltip + drawer card glossary */
  definition: string;
  /** Optional math/formula description shown after the definition */
  formula?: string;
}

/**
 * Free-form question-and-answer block.
 *
 * Some help content is authored as founder-approved prose that must ship
 * verbatim (see `loanBookValue`, whose copy is Section 11 of
 * docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md). Forcing that
 * into overview/useCases/cards would mean paraphrasing it, so those entries use
 * `sections` instead and leave the structured fields unset.
 */
export interface HelpSection {
  /** Stable identifier used for telemetry and React keys. */
  key: string;
  /** Section heading, rendered as the question. */
  heading: string;
  /** Section body. One or more paragraphs. */
  body: string;
}

export interface PageHelp {
  pageKey: PageHelpKey;
  /** Drawer title (typically matches the page <PageHeader title>) */
  title: string;
  /** 2–3 sentence plain-language overview, written for a non-technical broker principal */
  overview?: string;
  /** "When to use this page" bullets */
  useCases?: string[];
  /** Per-stat-card definitions */
  cards?: CardHelp[];
  /** Verbatim prose sections. Rendered in order, in place of overview/useCases/cards. */
  sections?: HelpSection[];
  /** Optional industry benchmarks (e.g. <8% / 8–12% / >12% trail run-off thresholds) */
  industryBenchmark?: { label: string; value: string; note: string }[];
  /** Methodology footnote — how the math works, edge cases, data sources */
  methodology?: string;
  /** Optional cross-links to neighbouring pages */
  relatedPages?: { label: string; href: string }[];
  /** ISO 8601 date — supports future content versioning */
  lastUpdated: string;
}

export const PAGE_HELP: Record<PageHelpKey, PageHelp> = {
  trailRunOff: {
    pageKey: 'trailRunOff',
    title: 'Broker Trail Run-Off',
    overview:
      'Track which brokers are losing trail commission month-over-month, and gauge the overall health of your trail book. Trail run-off measures the portion of your loan book that has stopped generating recurring trail commission — a leading indicator of refinancing, customer churn, or broker performance issues.',
    useCases: [
      'Spot brokers whose trail commission has dropped significantly before it shows up in revenue.',
      'Monitor the overall percentage of your loan book that has gone "stale" (no trail paid in 6+ months).',
      'Compare your portfolio run-off rate against the industry healthy benchmark of less than 8%.',
      'Identify candidates for proactive client outreach (loans about to refinance away).',
      'Trigger weekly email alerts when any broker drops more than your configured threshold.',
    ],
    cards: [
      {
        key: 'portfolioRunOffRate',
        title: 'Portfolio Run-Off Rate',
        definition:
          'The portion of your loan book by balance that has stopped paying trail commission in the last 6 months. Lower is healthier. A loan only counts here once it has gone 6 or more months without trail, so loans that dropped off recently still read as active; see Trail Book Change for the plain start-versus-end view.',
        formula:
          'Your Inactive Loan Balance divided by your Total Loan Book on Trail. Measured over a rolling 12 months and scoped to the loan book, office and broker you have selected.',
      },
      {
        key: 'trailBookChange',
        title: 'Trail Book Change',
        definition:
          'How much your trail book grew or shrank across the period: the loans and balance paying trail in the last month of the period compared with the first month. This counts every loan that stopped paying, including recent drops, so it will usually read higher than the run-off rate.',
        formula:
          'Closing month minus opening month, divided by the opening month, by balance (headline) and by loan count (subtitle). Scoped to the loan book, office and broker you have selected.',
      },
      {
        key: 'totalLoanBookOnTrail',
        title: 'Total Loan Book on Trail',
        definition:
          'Total outstanding loan balance across every loan that paid you trail commission at least once in the last 12 months — your trail-paying assets under management.',
        formula:
          'We add up the most recent balance we have on file for each loan, counting every loan only once even if it appeared in multiple commission runs.',
      },
      {
        key: 'activeLoanBalance',
        title: 'Active Loan Balance',
        definition:
          'The slice of your trail book whose most recent trail payment landed within the last 6 months. These loans are currently generating recurring revenue for you.',
        formula:
          'We add up the balances of loans whose most recent trail payment landed within the last 6 months of your uploaded data.',
      },
      {
        key: 'inactiveLoanBalance',
        title: 'Inactive Loan Balance',
        definition:
          'The slice of your trail book whose most recent trail payment was 6 or more months ago. Early-warning signal — these loans may have refinanced away, been discharged, or hit arrears.',
        formula:
          'We add up the balances of loans whose most recent trail payment was 6 or more months ago.',
      },
      {
        key: 'annualTrailRevenue',
        title: 'Annual Trail Revenue',
        definition:
          'Total trail commission your brokerage actually received across all loans in the last 12 months. This is real cash flow, not loan balance.',
        formula:
          'We add up every dollar of trail commission paid to you across all loans in the last 12 months. As a rule of thumb this comes out to about 0.15% of your Total Loan Book on Trail at industry-typical rates.',
      },
      {
        key: 'loansPayingTrail',
        title: 'Loans Paying Trail',
        definition:
          'Number of distinct loans that paid you trail commission at least once in the last 12 months. Pair this with Total Loan Book on Trail to see your average loan size.',
        formula:
          'We count each loan once that paid you any trail commission in the last 12 months.',
      },
    ],
    industryBenchmark: [
      { label: 'Healthy', value: '< 8%', note: 'Aaron Christie-David benchmark — trail book is retaining well.' },
      { label: 'Monitor', value: '8 – 12%', note: 'Some loans drifting; review broker activity and client outreach.' },
      { label: 'Needs attention', value: '> 12%', note: 'Significant trail erosion — investigate refinance leakage, broker churn, or upload gaps.' },
    ],
    methodology:
      'All figures come from your trail commission upload history over the last 12 months. We use the most recent payment record for each loan, so a loan that appears in multiple commission runs is only counted once. The 6-month line between Active and Inactive is anchored to the latest month we have data for — not today\'s calendar date — so if you upload monthly you\'ll see a steady rolling window. When you click Run Analysis Now, we only compare months where you\'ve uploaded the same set of runs. This prevents false alarms when (for example) Run 3 of the latest month hasn\'t been uploaded yet.',
    relatedPages: [
      { label: 'Loan Trail Drop-Off', href: '/reports/trail-drop-off' },
      { label: 'Trail Book Valuation', href: '/reports/trail-book-valuation' },
      { label: 'Trail Forecast', href: '/reports/trail-forecast' },
      { label: 'Discharged Loans', href: '/reports/discharged-loans' },
    ],
    lastUpdated: '2026-05-12',
  } as PageHelp,
  commissions: {
    pageKey: 'commissions',
    title: 'Commission Settings',
    overview:
      'The control room for how a broker is paid. Set the broker\'s upfront and trail split percentages, choose between a simple two-tier model and a three-tier model that includes a self-sourced rate, share a slice of the broker\'s earnings with broker assistants or credit analysts, tag individual loans by who introduced them, configure performance bonus targets, and reconcile month-by-month payouts. Every change you save creates a new point-in-time effective period — past history is preserved so you can always see who was on what rate when.',
    useCases: [
      'Configure a new broker\'s commission split (broker-introduction %, company-introduction %, monthly base salary).',
      'Switch a broker between the two-tier model and the three-tier model (which adds a separate self-sourced rate).',
      'Share part of the broker\'s earned commission with a broker assistant (the same share applied to both upfront and trail) or a credit analyst (you set the upfront and trail percentages separately).',
      'Tag individual loans as Broker Introduction, Company Introduction, or assigned to a Credit Analyst — this overrides the broker\'s default split for that one loan.',
      'Set performance bonus targets for the broker, an assistant, or a credit analyst — choose Settlement Volume, Loan Book Value, or Commission Earned; pick monthly or quarterly; add tiered bonuses ($X at the first milestone, $Y at the next).',
      'On the All Brokers overview, see year-to-date totals, monthly trends, and any assistants or credit analysts who are funded across multiple brokers.',
      'Add manual adjustments (claw-backs, deductions, one-off bonuses) and watch them flow through to the monthly pay reconciliation table.',
    ],
    cards: [
      {
        key: 'totalBrokerCommissionsYtd',
        title: 'Total Broker Commissions (YTD)',
        definition:
          'All-brokers overview. The total gross commission your brokers have earned together so far this financial year — before any splits with the company, assistants, or analysts.',
        formula:
          'We add up every dollar of commission earned across all your brokers from 1 July to today.',
      },
      {
        key: 'totalBaseSalariesMonthly',
        title: 'Total Base Salaries (Monthly)',
        definition:
          'Combined monthly base salary across all brokers — what your business is committed to paying every month before any commission top-up.',
        formula:
          'We add up the current active monthly base salary you have on file for each broker.',
      },
      {
        key: 'totalAmtOnTopOfBase',
        title: 'Total Amt On Top of Base',
        definition:
          'Year-to-date payments above base salary — the additional dollars your brokers earned beyond their fixed monthly base. Green when positive, red if any broker hasn\'t covered their base.',
        formula:
          'For each broker we work out how much their share of commission exceeded their salary year-to-date, then add it all up.',
      },
      {
        key: 'totalCarryOver',
        title: 'Total Carry Over',
        definition:
          'Outstanding shortfalls across all brokers — months where a broker\'s share of commission didn\'t cover their base salary. The deficit rolls forward against next month\'s earnings.',
        formula:
          'For each broker we tally the months they fell short and add the unpaid balance to next month\'s expected earnings.',
      },
      {
        key: 'brokerCommissionYtd',
        title: 'Broker Commission (YTD)',
        definition:
          'This broker\'s gross commission earned in the current financial year. Gross means before assistant or analyst shares are deducted.',
        formula:
          'We add up every dollar of commission earned by this broker from 1 July to today, counting loans matched by name and any historical aliases the broker has had.',
      },
      {
        key: 'baseSalaryMonthly',
        title: 'Base Salary (Monthly)',
        definition:
          'This broker\'s current active monthly base salary. Changes you save here create a new effective period and the previous salary stays in the history below — nothing is overwritten.',
      },
      {
        key: 'carryOverBalance',
        title: 'Carry Over Balance',
        definition:
          'This broker\'s outstanding shortfall — months where their share of commission didn\'t cover their base salary. The unpaid amount carries to the next month\'s expected earnings.',
        formula:
          'Each month we compare base salary to the broker\'s share of commission and accumulate any shortfall as a running balance.',
      },
      {
        key: 'untaggedLoans',
        title: 'Untagged Loans',
        definition:
          'Loans this broker received commission on but where you haven\'t yet picked an introduction type. Untagged loans default to Company Introduction (the lower share). If any were actually broker-introduced, tag them on the Loan Commission Tags panel below to apply the higher share.',
      },
    ],
    methodology:
      'All amounts on this page are point-in-time. When you save a change to a broker\'s split or base salary, the previous setting is preserved with an end-date and a new entry starts today — you can always look back to see who was on which rate in any past month. The two-tier model uses one broker-introduction percentage and one company-introduction percentage that apply to both upfront and trail. The three-tier model adds separate upfront and trail percentages for each tier (broker intro, company intro, self-sourced) and is opt-in per broker. Loan tags override the default split for individual loans. Assistant and credit-analyst shares come out of the broker\'s earned portion — not the company\'s share. There is a 100% cap on combined upfront and a separate 100% cap on combined trail (an assistant\'s share counts in both because the same rate applies uniformly). Performance bonus targets bound to a credit analyst measure combined progress across every broker the analyst is allocated on — only loans you\'ve explicitly tagged to the analyst contribute, and Settlement Volume and Loan Book Value targets show the gross amount of those loans (a tagged loan is binary — the analyst either brought it in or didn\'t). Commission Earned targets are scaled per broker by that broker\'s analyst share percentage. Manual adjustments write directly into the monthly reconciliation: claw-backs reduce the broker\'s share, deductions reduce gross commission before any split.',
    relatedPages: [
      { label: 'Manage Brokers', href: '/manage-brokers' },
      { label: 'Team Management', href: '/team' },
      { label: 'Office Dashboard', href: '/offices' },
      { label: 'Manage Offices', href: '/manage-offices' },
    ],
    lastUpdated: '2026-05-15',
  } as PageHelp,

  clientCare: {
    pageKey: 'clientCare',
    title: 'Client Care',
    overview:
      'Client Care turns your loan book into a living client database and tells each broker who is due for a personal touchpoint this week — settlement anniversaries, relationship reviews, fixed-rate roll-offs, birthdays and more. Every Monday morning each broker receives a short email listing their calls for the week, so no client slips through the cracks and your trail stays protected.',
    useCases: [
      'See every client across your loan book in one place, with the next touchpoint that is due for them.',
      'Work the weekly call list that is emailed to each broker every Monday morning.',
      'Log each call as you make it — it clears the client from next week’s list and records the contact on their file.',
      'Add clients who are not on the loan book, and fill in a client’s birthday, phone, email and notes.',
      'Managers and owners can review all client contact activity across the whole team.',
    ],
    cards: [
      {
        key: 'totalClients',
        title: 'Total Clients',
        definition:
          'Every client drawn from your loan book that you are responsible for caring for. A client is the borrower — or borrowers — named on the loan, and holds all of their loans, so a couple on a joint loan is counted once. This count reflects any filters you have applied above.',
      },
      {
        key: 'activeClients',
        title: 'Active',
        definition:
          'How many of the clients in the current view still have a live loan with you. Reflects any filters you have applied.',
      },
      {
        key: 'withEmail',
        title: 'With email',
        definition:
          'How many of the clients in the current view have an email address on file — a quick way to spot who still needs their details completed. Reflects any filters you have applied.',
      },
      {
        key: 'overdueToCall',
        title: 'Overdue to call',
        definition:
          'How many of the clients in the current view have a touchpoint that is already overdue — the same clients shown in the Overdue column of the call list. Reflects any filters you have applied.',
      },
    ],
    methodology:
      'Your client list is built automatically from your loan book. Each client is the borrower — or borrowers — named on the loan, and holds every loan under that name, so a couple on a joint loan is treated as a single client. If the same person appears twice because their name was written differently on different loans (for example on their own loan and a joint one), you can combine them with Resolve Identity. Each broker sees only their own clients; an assistant or credit analyst sees the broker (or brokers) they support; managers and owners see everyone. Contact details and birthdays are filled in by hand on each client’s profile. Reminders only ever go to the people working the account; clients are never emailed.',
    relatedPages: [
      { label: 'Client Care Settings', href: '/client-care-settings' },
      { label: 'Contact Activity', href: '/contact-activity' },
    ],
    lastUpdated: '2026-06-03',
  } as PageHelp,

  clientCareSettings: {
    pageKey: 'clientCareSettings',
    title: 'Client Care Settings',
    overview:
      'Decide how and when Client Care reaches out. Configure touchpoint timing, choose the named managers who receive the weekly overdue-call escalation summary, and control email or team-chat delivery without changing each broker’s own call digest.',
    useCases: [
      'Choose which touchpoints matter to your office — from settlement anniversaries and relationship reviews to fixed-rate roll-offs and birthday calls.',
      'Set how often a recurring relationship review repeats, for example every six or twelve months.',
      'Decide how long a broker has to act on a reminder before their manager is brought in.',
      'Choose specific active managers for escalation summaries while keeping owner-brokers on their own call lists only.',
      'Choose whether reminders are sent by email, by team chat, or both.',
    ],
    cards: [],
    methodology:
      'Recurring touchpoints such as anniversaries and reviews are scheduled automatically from your loan book. A touchpoint clears when the broker logs the call. Named escalation recipients receive one Monday summary and only see overdue items within their existing company or office access. If a selected account later becomes ineligible, Valutten does not fall back to an unselected owner and raises an operational warning.',
    relatedPages: [{ label: 'Client Care', href: '/clients' }],
    lastUpdated: '2026-08-21',
  } as PageHelp,
  team: {
    pageKey: 'team',
    title: 'Team Management',
    overview:
      'Invite and manage everyone who can access your workspace, and control exactly what each person can see. Assign a role, scope a user to specific offices, reports and loan books, hide commission figures for salaried staff, and link one login to several brokers so a single person can service multiple books without separate logins.',
    useCases: [
      'Invite a broker, broker assistant, credit analyst, administrator, office admin or marketing user, scoped to the right offices.',
      'Restrict a broker-family user to specific reports and loan books, or hide commission dollar amounts for salaried staff with the $ Hidden toggle.',
      'Link a broker, broker assistant or credit analyst to other brokers (a departed broker, an acquired book, or an owner whose clients are serviced by several people) from a user’s Permissions, so they see those books without earning commission on them.',
      'Approve a broker connecting their login to their commission data, and hand a departing broker’s loan book to a successor under Broker Assignments.',
      'Keep operational credit-analyst records that do not need a login under Operational Roles.',
    ],
    cards: [],
    methodology:
      'Roles, from most to least access: super_admin, administrator, office_admin, credit_analyst, broker, marketing, broker_assistant. Only a super_admin can promote someone to administrator. Linked Brokers (set in a user’s Permissions, super_admin or administrator only) add the chosen brokers’ books to that user’s loans and portfolio views; they never add to the user’s own commission earnings, which always reflect only that person’s own attributed clients. The $ Hidden toggle strips dollar figures for salaried staff, and Report and Loan Book access narrow what a broker-family user can open.',
    relatedPages: [{ label: 'Commission Settings', href: '/broker-commission-settings' }],
    lastUpdated: '2026-06-18',
  } as PageHelp,
  // ---------------------------------------------------------------------------
  // Loan Book value, entity scope and valuation tiers.
  //
  // COPY IS FROZEN. Every string in `sections` below is Section 11 of
  // docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md. It is
  // founder-approved wording written after a customer-facing number ($78.43M
  // against Loan Market's $64.80M) was queried mid staff-review. Do not
  // paraphrase, condense or "improve" it. Three deviations from the source, all
  // signed off, all recorded here so nobody has to guess later:
  //
  //   1. "whichBook" opens "the loans written under one lending entity" where
  //      the handover said "the loans in your own office's lending entity". The
  //      original implied a broker's CURRENT office, which is wrong for anyone
  //      who has moved: Section 5 of the handover shows Vincent Moore and
  //      Candice Joseph holding most of their book under a legacy entity.
  //   2. Markdown emphasis markers around one clause in "Why doesn't this match
  //      the Loan Market report?" are dropped; the drawer renders plain text.
  //      No words altered.
  //   3. Apostrophes are typographic, matching every other entry in this file.
  //      Unrelated to the repo-wide dash rule, which concerns dashes only.
  //   4. (2026-08-20) The AGGREGATOR NOUN is tenant-generic: "the Loan Market
  //      report" / "the LM report" / "the LM portfolio" are now "your
  //      aggregator's report" / "the aggregator's portfolio", and the worked
  //      example "Mornington (ENTM100)" is gone. ENTM100 is an ENTOURAGE entity
  //      code, so every non-Loan-Market tenant was shown another tenant's
  //      lending entity as the example and a competitor's brand as the thing
  //      their book should reconcile to. Only the brand nouns moved: the
  //      reasoning in every sentence is character-identical, which is what the
  //      freeze exists to protect.
  //
  // Each string is pinned character for character by
  // src/config/__tests__/loanBookHelpContent.test.tsx.
  // ---------------------------------------------------------------------------
  loanBookValue: {
    pageKey: 'loanBookValue',
    title: 'Loan Book',
    sections: [
      {
        key: 'whichBook',
        heading: 'Which book am I looking at?',
        body: 'Primary loan book shows the loans written under one lending entity, named explicitly in the loan book selector. This is the view that lines up with your aggregator’s report for that entity. All loan books shows every entity you have loans in, including books you have moved on from and any acquired trail. The two differ if you have worked across more than one office.',
      },
      {
        key: 'whenCounts',
        heading: 'When does a loan count?',
        body: 'A loan counts once the lender has paid trail commission on it. Upfront commission alone does not put a loan in your book: upfront is paid at settlement, whereas trail is the lender confirming month by month that they still hold the loan. Loans that have settled but not yet reached their first trail cycle are shown separately as "Settled, trail not yet started".',
      },
      {
        key: 'howValued',
        heading: 'How is a loan valued?',
        body: 'We use the most recent outstanding balance the lender reported. Where a loan has several splits or facilities, we add them together, so one loan can contribute several balances. Where the lender has never reported a balance, we fall back to the original settled amount. That is an estimate, it does not reduce as the loan is paid down, and it will read high. The number of estimated loans is shown so you know how much of your book is estimated.',
      },
      {
        // "two months" is CORRECT and is NOT a typo for the flag value.
        // `featureFlags.loan_book_trail_recency_months` is ratified at 1, but
        // the builder's N is an inclusive look-back, so the window spans N+1
        // months: the current month M and M-1. A loan leaves the book only when
        // it paid trail in neither, which is what a broker observes as "stopped
        // paying trail for two months". The flag integer and the user-visible
        // window legitimately differ by one. DO NOT sync this wording to the
        // flag value. Numbering note: Section 9 of the 2026-08-04 handover.
        key: 'whenLeaves',
        heading: 'When does a loan leave my book?',
        body: 'When the lender reports it as discharged, or when it stops paying trail for two months, which usually means it has been refinanced or paid out without a discharge notice reaching us.',
      },
      {
        key: 'whyMismatch',
        heading: 'Why doesn’t this match my aggregator’s report?',
        body: 'Three reasons, all expected. Your aggregator’s report covers one lending entity at a time, so use Primary loan book to compare like for like. Loans you settled in the last few weeks are in your book here before they appear in theirs. And we remove a discharged loan in the month it is paid out, whereas the aggregator’s portfolio takes about a month to catch up, so shortly after a discharge our figure will be lower and ours is the current one.',
      },
      {
        key: 'howCurrent',
        heading: 'How current is this?',
        body: 'Each entity updates when its commission file for the month is processed. The as-at date is shown per entity. An entity that has not filed yet shows its previous month’s position.',
      },
    ],
    lastUpdated: '2026-08-04',
  } as PageHelp,
};

/**
 * Resolve help content for a specific stat card on a specific page.
 *
 * @example
 *   <StatCard
 *     title="Annual Trail Revenue"
 *     helpKey="annualTrailRevenue"
 *     helpTooltip={cardHelp('trailRunOff', 'annualTrailRevenue')}
 *     ...
 *   />
 */
export function cardHelp(
  pageKey: PageHelpKey,
  cardKey: string,
): { definition: string; formula?: string } | undefined {
  const card = PAGE_HELP[pageKey]?.cards?.find((c) => c.key === cardKey);
  return card ? { definition: card.definition, formula: card.formula } : undefined;
}

// ============================================================================
// ROLE PERMISSIONS / FAQ / GUIDES / SUPPORT (existing)
// ============================================================================

// ---------- Role Permissions ----------

export interface Permission {
  label: string;
  roles: AppRole[];
  /** Optional note shown alongside the permission */
  note?: string;
  /** Subscription tier required (if any) */
  tier?: 'plus' | 'pro' | 'enterprise';
}

export interface PermissionCategory {
  category: string;
  permissions: Permission[];
}

export const ROLE_PERMISSIONS: PermissionCategory[] = [
  {
    category: 'Dashboards',
    permissions: [
      { label: 'Main Dashboard', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: blocked if hideCommissions is enabled' },
      { label: 'Office Dashboard', roles: ['super_admin', 'administrator', 'office_admin'], tier: 'pro', note: 'Scoped to assigned offices' },
      { label: 'Office Comparison', roles: ['super_admin', 'administrator', 'office_admin'] },
      { label: 'Broker Dashboard', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: own data only' },
      { label: 'Commission Breakdown', roles: ['super_admin', 'administrator', 'office_admin'] },
    ],
  },
  {
    category: 'Data Scope',
    permissions: [
      { label: 'Company-wide data', roles: ['super_admin'], note: 'Administrator: only if canSeeAllOffices is enabled' },
      { label: 'Cross-office data', roles: ['super_admin', 'administrator', 'office_admin'], note: 'Scoped to assigned offices' },
      { label: 'All brokers in scope', roles: ['super_admin', 'administrator', 'office_admin'], note: 'Broker: own data only' },
    ],
  },
  {
    category: 'Uploads & Management',
    permissions: [
      { label: 'Upload Commissions', roles: ['super_admin', 'administrator', 'office_admin'] },
      { label: 'Delete Source Document', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: own uploads only' },
      { label: 'Manage Team & Invite Users', roles: ['super_admin', 'administrator', 'office_admin'] },
      { label: 'Create / Delete Office', roles: ['super_admin', 'administrator'] },
      { label: 'Manage Brokers Page', roles: ['super_admin', 'administrator', 'office_admin'] },
    ],
  },
  {
    category: 'Reports — Trail Book',
    permissions: [
      { label: 'Broker Trail Run-Off', roles: ['super_admin', 'administrator', 'office_admin'] },
      { label: 'Loan Trail Drop-Off', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], tier: 'pro', note: 'Broker: per allowedReports' },
      { label: 'Trail Book Valuation', roles: ['super_admin', 'administrator', 'office_admin'], tier: 'pro' },
      { label: 'Trail Forecast', roles: ['super_admin', 'administrator', 'office_admin'], tier: 'pro' },
      { label: 'Book Health', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], tier: 'pro', note: 'Broker: per allowedReports' },
      { label: 'Discharged Loans', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: per allowedReports' },
    ],
  },
  {
    category: 'Reports — Income & Payments',
    permissions: [
      { label: 'Settlement Age', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: per allowedReports' },
      { label: 'Clawbacks', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: per allowedReports' },
      { label: 'Arrears', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: per allowedReports' },
      { label: 'RCTI Cashflow', roles: ['super_admin', 'administrator', 'office_admin'], tier: 'pro' },
    ],
  },
  {
    category: 'Reports — Portfolio',
    permissions: [
      { label: 'Loan Portfolio', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], note: 'Broker: per allowedReports' },
      { label: 'Customer Portfolio', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], tier: 'pro', note: 'Broker: per allowedReports' },
      { label: 'Client Retention', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], tier: 'enterprise', note: 'Broker: per allowedReports' },
      { label: 'Acquired Books', roles: ['super_admin', 'administrator', 'office_admin', 'broker'], tier: 'enterprise', note: 'Broker: per allowedReports' },
      { label: 'Broker Loan Books', roles: ['super_admin', 'administrator'], tier: 'enterprise' },
    ],
  },
  {
    category: 'Enterprise Features',
    permissions: [
      { label: 'Income Summary', roles: ['super_admin', 'administrator'], tier: 'enterprise' },
      { label: 'Acquisition Analysis', roles: ['super_admin', 'administrator'], tier: 'enterprise', note: 'Administrator: own analyses only' },
      { label: 'AI Assistant', roles: ['super_admin', 'administrator'], tier: 'enterprise' },
      { label: 'Client Groups', roles: ['super_admin', 'administrator'] },
      { label: 'Contact Activity', roles: ['super_admin', 'administrator'] },
      { label: 'Loan Books (Manage Templates)', roles: ['super_admin', 'administrator'] },
    ],
  },
  {
    category: 'Settings & Billing',
    permissions: [
      { label: 'Personal Settings', roles: ['super_admin', 'administrator', 'office_admin', 'broker'] },
      { label: 'Audit Log', roles: ['super_admin', 'administrator', 'office_admin'] },
      { label: 'Billing & Subscription', roles: ['super_admin'] },
    ],
  },
];

// ---------- Role Descriptions ----------

export const ROLE_DESCRIPTIONS: Record<AppRole, { title: string; summary: string }> = {
  super_admin: {
    title: 'Super Admin',
    summary: 'Full company-wide access including billing, all reports, and all management features.',
  },
  administrator: {
    title: 'Administrator',
    summary: 'Manages team, offices, and advanced reports. Data scoped to assigned offices unless canSeeAllOffices is enabled.',
  },
  office_admin: {
    title: 'Office Admin',
    summary: 'Manages brokers and uploads within assigned offices. Access to standard reports and dashboards.',
  },
  credit_analyst: {
    title: 'Credit Analyst',
    summary: 'Helps brokers with credit assessment and earns commission attributed to their name. Optionally linked to one or more brokers by admin to inherit their data scope.',
  },
  broker: {
    title: 'Broker',
    summary: 'Views own commission data and permitted reports. Some features may be restricted by your admin.',
  },
  marketing: {
    title: 'Marketing',
    summary: 'CRM operator focused on the Lead Inbox: read/write leads, assign to brokers, configure routing. Reports and dashboards opt-in via admin.',
  },
};

// ---------- FAQ Entries ----------

export interface FaqEntry {
  question: string;
  answer: string;
  /** Roles that should see this FAQ (undefined = all roles) */
  roles?: AppRole[];
}

export const FAQ_ENTRIES: FaqEntry[] = [
  {
    question: 'What does "per allowedReports" mean?',
    answer: 'Your admin can configure which reports you can access. If no specific reports are set, you have access to all reports available to your role. Check with your admin if a report is not visible.',
    roles: ['broker'],
  },
  {
    question: 'What is canSeeAllOffices?',
    answer: 'Administrators are scoped to their assigned offices by default. A Super Admin can enable "canSeeAllOffices" on your account to grant company-wide data access.',
    roles: ['administrator'],
  },
  {
    question: 'Why can\'t I see billing?',
    answer: 'Billing and subscription management is restricted to the Super Admin role. Contact your Super Admin if you need to make billing changes.',
    roles: ['administrator', 'office_admin'],
  },
  {
    question: 'What is hideCommissions?',
    answer: 'When enabled on a broker account, this hides all dollar amounts and blocks access to the main dashboard. It\'s set by an admin during invitation or later in broker settings.',
    roles: ['broker'],
  },
  {
    question: 'How are commission runs numbered?',
    answer: 'Runs are detected automatically based on the upload date: Days 1-10 = Run 1, Days 11-20 = Run 2, Days 21-31 = Run 3. Some loan books use a single run per month instead of three.',
  },
  {
    question: 'What subscription tier am I on?',
    answer: 'Check the "Plans & Features" tab on this page to see your current plan and what\'s included. Your Super Admin can upgrade your plan from the Billing page.',
  },
];

// ---------- Guides ----------

export interface Guide {
  title: string;
  content: string;
  /** Roles that should see this guide (undefined = all roles) */
  roles?: AppRole[];
}

export const GUIDES: Guide[] = [
  {
    title: 'How Runs Work',
    content: 'Commission files are grouped into runs based on when they are uploaded each month. Run 1 covers days 1-10, Run 2 covers days 11-20, and Run 3 covers days 21-31. Some loan books use a "single" run strategy where all data goes into Run 1. The rolling 3-run window on your dashboard shows the latest 3 run slots, giving you a current snapshot of commission performance.',
  },
  {
    title: 'Understanding Commission Types',
    content: 'Upfront Commission is the initial payment received when a loan settles. Trail Commission is the ongoing monthly payment for maintaining the loan. Arrears Commission is trail paid in lump sums (quarterly or annually). Clawbacks occur when a borrower discharges their loan within the clawback period — the lender reclaims part of the upfront commission.',
  },
  {
    title: 'What is Line of Trail (LOT)?',
    content: 'Line of Trail counts the number of distinct loans currently generating trail, upfront, or arrears commissions within your rolling 3-run window. It measures the health and breadth of your loan book — a growing LOT means your recurring revenue base is expanding.',
  },
  {
    title: 'Uploading Commission Files',
    roles: ['super_admin', 'administrator', 'office_admin'],
    content: 'Go to the Upload page to upload commission files. The recommended method is RCTI + CSV paired upload. Name files in the format "RUN X - Month YYYY" (e.g., "RUN 1 - January 2026"). The system auto-detects the period and run number. You can also upload ZIP archives containing multiple files. Multi-entity uploads are available on Pro tier and above.',
  },
  {
    title: 'Managing Your Team',
    roles: ['super_admin', 'administrator', 'office_admin'],
    content: 'Use the Team Management page to invite new users. When inviting, you choose their role (broker, office admin, or administrator) and assign them to offices. For brokers, you can optionally set hideCommissions to hide dollar values, or restrict their report access via allowedReports. Brokers are automatically linked to their data using name matching.',
  },
];

// ---------- Support Channels ----------

export interface SupportChannel {
  title: string;
  description: string;
  email: string;
  /** SLA by tier in hours */
  sla: Record<'plus' | 'pro' | 'enterprise', string>;
}

export const SUPPORT_CHANNELS: SupportChannel[] = [
  {
    title: 'General Support',
    description: 'Questions about features, data, or troubleshooting.',
    email: 'support@valutten.com',
    sla: { plus: '48 hours', pro: '24 hours', enterprise: '8 hours' },
  },
  {
    title: 'Billing & Subscription',
    description: 'Invoices, plan changes, and payment issues.',
    email: 'billing@valutten.com',
    sla: { plus: '48 hours', pro: '24 hours', enterprise: '8 hours' },
  },
  {
    title: 'Privacy & Compliance',
    description: 'Data privacy requests and regulatory compliance.',
    email: 'privacy@valutten.com',
    sla: { plus: '48 hours', pro: '24 hours', enterprise: '8 hours' },
  },
];
